import useCookies from '@settings/features/settingsOverview/features/settingsOverviewCookies/hooks/useCookies';
import { ECookie } from '@settings/features/settingsOverview/features/settingsOverviewCookies/hooks/useCookies/useCookies.enums';
import { cookiesData } from '@settings/features/settingsOverview/features/settingsOverviewCookies/hooks/useCookies/useCookies.variables';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC, useEffect } from 'react';
import Switch from 'src/components/common/Switch';
import { getCookie } from 'src/functions/cookies';
import Flex from 'src/styles/layouts/Flex';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import NGButton from 'src/styles/theme/components/NGButton';
import NGHeading from 'src/styles/theme/components/NGHeading';
import NGText from 'src/styles/theme/components/NGText';
import { ETextAlign, ETextType } from 'src/styles/theme/components/NGText/NGText.enum';
import { EOverflow, ESize } from 'src/styles/theme/utils/enums';
import AnimatedCookieDescription from '../AnimatedCookieDescription';
import { NGSeparatorEx, StyledCookieSettings, StyledSwitchContainer } from './CookieSettings.styles';
import { ICookieSettings } from './CookieSettings.type';

const CookieSettings: FC<ICookieSettings> = ({ autoSave, onHideCookieBanner = () => {} }) => {
	const { acceptAllCookies, rejectAllCookies, updateActivatedCookie, activatedCookiesType, isReloadNeeded } =
		useCookies();

	const router = useRouter();

	const onCloseModal = (isAcceptAll?: boolean) => {
		onHideCookieBanner();

		if ((!autoSave && isAcceptAll) || isReloadNeeded) {
			router.reload();
		}
	};

	const handleRejectAllCookies = () => {
		rejectAllCookies();
		onCloseModal();
	};

	const handleAcceptAllCookies = () => {
		acceptAllCookies();

		onCloseModal(true);
	};

	useEffect(() => {
		if (autoSave) {
			function reloadIfNeeded() {
				if (!getCookie(ECookie.isReloadNeeded)) return;

				router.reload();
			}

			router.events.on('routeChangeComplete', reloadIfNeeded);

			return () => {
				router.events.off('routeChangeComplete', reloadIfNeeded);
			};
		}
	}, [router, autoSave]);

	return (
		<StyledCookieSettings>
			<NGHeading textAlign={ETextAlign.center} type={ETextType.h3}>
				Cookie Settings
			</NGHeading>
			<NGSeparatorEx marginSize={ESize.m} rhombusSize={ESize.xs} />

			<Flex fullWidth overflowY={EOverflow.auto} direction={EFlex.column} flex='1'>
				{cookiesData.map(({ id, dataCy, type, isDisabled, defaultValue, label, loreDescription, legalDescription }) => (
					<StyledSwitchContainer key={id}>
						<Switch
							customId={id}
							dataCy={dataCy}
							disabled={isDisabled}
							isChecked={isDisabled ? !!defaultValue : activatedCookiesType.includes(type)}
							setIsChecked={() => updateActivatedCookie(type)}
							label={label}
						/>

						<AnimatedCookieDescription
							htmlFor={id}
							loreDescription={loreDescription}
							legalDescription={legalDescription}
						/>
					</StyledSwitchContainer>
				))}
			</Flex>

			<NGText size={ESize.m} marginTop={ESize['4xs']}>
				For more information on exactly which cookies we use and why, see our{' '}
				<Link href='/legal/privacy-policy'>
					<a className='richtext rt-link' target='_blank'>
						privacy policy
					</a>
				</Link>{' '}
				and{' '}
				<Link href='/legal/terms-of-service'>
					<a className='richtext rt-link' target='_blank'>
						terms of service
					</a>
				</Link>
				.
			</NGText>

			{!autoSave ? (
				<Flex fullWidth horizontal={EFlex.between} gap={ESize['4xs']} marginTop={ESize.m}>
					<NGButton
						dataCy='cookies-refuse-all'
						onClick={handleRejectAllCookies}
						size={ESize.s}
						smPaddingX={ESize['3xs']}
						width='100%'
					>
						Refuse All
					</NGButton>

					<NGButton
						dataCy='cookies-accept-selection'
						onClick={() => onCloseModal()}
						size={ESize.s}
						smPaddingX={ESize['3xs']}
						width='100%'
					>
						Accept Selection
					</NGButton>

					<NGButton
						dataCy='cookies-accept-all'
						onClick={handleAcceptAllCookies}
						size={ESize.s}
						smPaddingX={ESize['3xs']}
						width='100%'
					>
						Accept All
					</NGButton>
				</Flex>
			) : null}
		</StyledCookieSettings>
	);
};

export { CookieSettings };
